import { useState, FormEvent, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";
import { getSupabase } from "@/lib/supabase";
import { trackEvent } from "@/lib/analytics";

interface LocationState {
  subject?: string;
}

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: `${siteUrl}/contact`,
  name: "Contact Hackers Lodge",
  description: "Get in touch with Hackers Lodge. Have a private request? Contact us directly.",
  mainEntity: {
    "@type": "Organization",
    name: "Hackers Lodge",
    email: "support@hackerslodge.com",
    url: siteUrl,
  },
};

export default function ContactPage() {
  const location = useLocation();
  const state = (location.state as LocationState) || {};
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [subject, setSubject] = useState(state.subject || "");
  const formStartedRef = useRef(false);

  useEffect(() => {
    if (state.subject) {
      setSubject(state.subject);
    }
  }, [state.subject]);

  const handleFormFocus = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackEvent("contact_form_start");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("idle");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    // Remove honeypot from payload
    formData.delete("website_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9bansp4iotnakcn9gm0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      if (response.ok && parsed?.code === "OK") {
        // Fire GA4 conversion: contact_form
        trackEvent("contact_form", {
          subject: (formData.get("subject") as string) || "",
        });

        setStatus("success");
        form.reset();
        setSubject("");

        // Fire contact notification email via Supabase edge function (non-blocking)
        try {
          const supabase = getSupabase();
          supabase.functions.invoke("contact-notify", {
            body: {
              name: formData.get("name") as string || "",
              email: formData.get("email") as string || "",
              subject: formData.get("subject") as string || "",
              message: formData.get("message") as string || "",
            },
          }).catch(() => {});
        } catch {
          // Silently fail — form data is still captured
        }
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Contact Hackers Lodge — Get in Touch with Our Cybersecurity Team"
        description="Have a question or a private project request? Contact Hackers Lodge directly. Our cybersecurity concierge team will get back to you within 24 hours. Secure and confidential communication."
        keywords="contact hackers lodge, hire hacker contact, cybersecurity services contact, ethical hacking inquiry, penetration testing contact"
        canonicalUrl={`${siteUrl}/contact`}
        ogType="website"
        schema={contactSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="py-12 md:py-16 bg-[#161b22]">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
            <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
            <div className="mb-8 text-center">
              <h1 className="text-2xl md:text-3xl font-light text-gray-200 mb-2">
                Contact Us
              </h1>
              <p className="text-sm text-gray-500">
                Have a private request? Get in touch with us directly.
              </p>
            </div>

            <div className="bg-[#0d1117] rounded border border-gray-700 p-6 md:p-8">
              {status === "success" && (
                <div className="mb-6 p-4 bg-green-900/20 border border-green-700/30 rounded text-green-400 text-sm">
                  <i className="fas fa-check-circle mr-2" />
                  Thank you! Your message has been sent successfully. We will get back to you soon.
                </div>
              )}

              {status === "error" && (
                <div className="mb-6 p-4 bg-red-900/20 border border-red-700/30 rounded text-red-400 text-sm">
                  <i className="fas fa-exclamation-circle mr-2" />
                  {errorMsg}
                </div>
              )}

              <form
                data-readdy-form
                id="d9bansp4iotnakcn9gm0"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      onFocus={handleFormFocus}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-300 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What is this about?"
                    className="w-full px-4 py-3 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="Describe your request in detail..."
                    className="w-full px-4 py-3 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de] resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="inline-block bg-[#5bc0de] hover:bg-[#46b8da] text-white text-sm font-semibold px-8 py-3 rounded transition-all whitespace-nowrap"
                  >
                    <i className="fas fa-paper-plane mr-2" />
                    Send Message
                  </button>
                </div>
              </form>

              {/* Direct Email */}
              <div className="mt-6 pt-5 border-t border-gray-700">
                <p className="text-sm text-gray-500 text-center">
                  Or reach us directly at{" "}
                  <a href="mailto:support@hackerslodge.com" className="text-[#5bc0de] font-semibold hover:underline">
                    support@hackerslodge.com
                  </a>
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-[#5bc0de]/10">
                  <i className="fas fa-envelope text-[#5bc0de]" />
                </div>
                <h3 className="text-sm font-semibold text-gray-200 mb-1">Email Us</h3>
                <p className="text-xs text-gray-500">support@hackerslodge.com</p>
              </div>
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-[#5cb85c]/10">
                  <i className="fas fa-shield-alt text-[#5cb85c]" />
                </div>
                <h3 className="text-sm font-semibold text-gray-200 mb-1">Secure Communication</h3>
                <p className="text-xs text-gray-500">All messages are encrypted</p>
              </div>
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-[#5bc0de]/10">
                  <i className="fas fa-clock text-[#5bc0de]" />
                </div>
                <h3 className="text-sm font-semibold text-gray-200 mb-1">Response Time</h3>
                <p className="text-xs text-gray-500">Within 24 hours</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}